import { useEffect, useState } from 'react'

import {
  Alert,
  Button,
  Center,
  Code,
  CopyButton,
  Loader,
  Stack,
  Text,
} from '@mantine/core'
import { createFileRoute } from '@tanstack/react-router'
import Userfront from '@userfront/core'

export const Route = createFileRoute('/_app/cli/auth')({
  component: CliAuthPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect_uri: string; state?: string } => ({
    redirect_uri:
      typeof search.redirect_uri === 'string' ? search.redirect_uri : '',
    state: typeof search.state === 'string' ? search.state : undefined,
  }),
})

function isLocalhostUri(uri: string): boolean {
  try {
    const parsed = new URL(uri)
    return (
      parsed.protocol === 'http:' &&
      ['localhost', '127.0.0.1', '::1', '[::1]'].includes(parsed.hostname)
    )
  } catch {
    return false
  }
}

function CliAuthPage() {
  const { redirect_uri, state } = Route.useSearch()
  const [error, setError] = useState<string>()
  const [redirected, setRedirected] = useState(false)

  const token = Userfront.tokens.accessToken

  useEffect(() => {
    if (!redirect_uri || !isLocalhostUri(redirect_uri)) {
      setError(
        'Invalid redirect URI. Only localhost URLs (http://localhost, http://127.0.0.1, http://[::1]) are allowed.',
      )
      return
    }

    if (!token) {
      setError(
        'Authentication token is not available. Please try logging in again.',
      )
      return
    }

    const url = new URL(redirect_uri)
    url.searchParams.set('token', token)
    if (state) {
      url.searchParams.set('state', state)
    }

    setRedirected(true)
    window.location.href = url.toString()
  }, [redirect_uri, state, token])

  if (error) {
    return (
      <Center mih="60vh">
        <Stack align="center" gap="md" maw={500}>
          <Alert color="red" title="CLI Authentication Error" w="100%">
            {error}
          </Alert>
          {token && (
            <Stack align="center" gap="xs">
              <Text size="sm" c="dimmed">
                You can manually copy your access token:
              </Text>
              <Code block style={{ wordBreak: 'break-all' }}>
                {token}
              </Code>
              <CopyButton value={token}>
                {({ copied, copy }) => (
                  <Button
                    variant="light"
                    size="xs"
                    onClick={copy}
                    color={copied ? 'teal' : 'blue'}
                  >
                    {copied ? 'Copied' : 'Copy token'}
                  </Button>
                )}
              </CopyButton>
            </Stack>
          )}
        </Stack>
      </Center>
    )
  }

  if (redirected) {
    return (
      <Center mih="60vh">
        <Stack align="center" gap="xs">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Redirecting to CLI...
          </Text>
        </Stack>
      </Center>
    )
  }

  return (
    <Center mih="60vh">
      <Stack align="center" gap="xs">
        <Loader size="md" />
        <Text size="sm" c="dimmed">
          Preparing authentication...
        </Text>
      </Stack>
    </Center>
  )
}
